import { makeStyles, FormControlLabel, Checkbox, Tooltip } from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import React from "react";

import { LogFilterRequestFormObj } from "../../hooks/useLogFilterRequest";
import { PaginationControls } from "../PaginationControls";
import { SearchBar } from "../SearchBar";
import { SearchSelect } from "../SearchSelect";
import { useApi } from "../useApi";

import { TimeZoneSwitch } from "./TimeZoneSwitch";
import { EventTypeAndNameSelect } from "../pages/events/EventTypeAndNameSelect";
import { EventTypeOption } from "../pages/events/ManagementEventOptions";

const useStyles = makeStyles(() => ({
    filterRow: {
        display: "flex",
        justifyContent: "start",
        alignItems: "flex-end",
        gap: "10px",
        maxWidth: "100%",
        flexWrap: "wrap",
    },
    buttonContainer: {
        display: "flex",
        gap: "10px",
        marginTop: "20px",
        marginBottom: "10px",
    },
}));

interface Props {
    formObj: LogFilterRequestFormObj;
    setFormObj: React.Dispatch<React.SetStateAction<LogFilterRequestFormObj>>;
    resetFormObj: () => void;
    dealerId: number | undefined;
    organizationId: number | undefined;
    siteId: number | undefined;
    logTypeOptions: EventTypeOption[];
    numberOfPages: number;
    logTypeSelectLabel?: string;
    logNameSelectLabel?: string;
    buttons?: React.ReactNode;
    buttonsInPaginationControls?: React.ReactNode;
    paginationDisabled: boolean;
    allowMultipleEventTypes?: boolean;
}

export function LogFilterRequestSection({
    formObj,
    setFormObj,
    resetFormObj,
    dealerId,
    organizationId,
    siteId,
    logTypeOptions,
    numberOfPages,
    logTypeSelectLabel = "Event Type",
    logNameSelectLabel = "Event",
    buttons,
    buttonsInPaginationControls,
    paginationDisabled,
    allowMultipleEventTypes = false
}: Props) {
    const classes = useStyles();
    const { employeeApi, customerApi } = useApi();

    return (<>
        <div>
            <SearchBar
                defaultSearchText={formObj.searchText}
                search={searchText => setFormObj(
                    prev => ({ ...prev, searchText })
                )}
                onReset={resetFormObj}
            />

            <SearchSelect
                label="Employee"
                selectedValue={formObj.employeeToSearch}
                setSelectedValue={employeeToSearch => setFormObj(
                    prev => ({ ...prev, employeeToSearch })
                )}
                dealerId={dealerId}
                organizationId={organizationId}
                siteId={siteId}
                searchMethod={employeeApi.getNameMatches}
            />

            <SearchSelect
                label="Customer"
                selectedValue={formObj.customerToSearch}
                setSelectedValue={customerToSearch => setFormObj(
                    prev => ({ ...prev, customerToSearch })
                )}
                dealerId={dealerId}
                organizationId={organizationId}
                siteId={siteId}
                searchMethod={customerApi.getNameMatches}
            />

            <div className={classes.filterRow}>
                <DatePicker
                    value={formObj.from}
                    onChange={momentFrom => setFormObj(
                        prev => ({ ...prev, from: momentFrom && momentFrom.toISOString() })
                    )}
                    label="From"
                    format="MMM D, YYYY"
                />
                <DatePicker
                    value={formObj.to}
                    onChange={momentTo => setFormObj(
                        prev => ({ ...prev, to: momentTo && momentTo.toISOString() })
                    )}
                    label="To"
                    format="MMM D, YYYY"
                />

                <TimeZoneSwitch
                    localTimeZoneSelected={formObj.filterByLocalTime}
                    onLocalTimeZoneSelected={filterByLocalTime => setFormObj(
                        prev => ({ ...prev, filterByLocalTime })
                    )}
                />

                <Tooltip title="Filter results to only show events that occurred while the site was in maintenance mode">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formObj.wasInMaintenanceMode === true}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFormObj(
                                        prev => ({ ...prev, wasInMaintenanceMode: checked ? true : null })
                                    );
                                }}
                                color="primary"
                            />
                        }
                        label="Maintenance Mode"
                    />
                </Tooltip>

                <EventTypeAndNameSelect
                    value={formObj.eventTypes || []}
                    onChange={eventTypes => setFormObj(
                        prev => ({ ...prev, eventTypes })
                    )}
                    eventTypeOptions={logTypeOptions}
                    eventTypeLabel={logTypeSelectLabel}
                    eventNameLabel={logNameSelectLabel}
                    isMultiSelect={allowMultipleEventTypes}
                />
            </div>
        </div>

        {buttons && (
            <div className={classes.buttonContainer}>
                {buttons}
            </div>
        )}

        <PaginationControls
            page={formObj.page}
            setPage={page => setFormObj(
                prev => ({ ...prev, page })
            )}
            pageCount={numberOfPages}
            itemsPerPage={formObj.itemsPerPage}
            setItemsPerPage={itemsPerPage => setFormObj(
                prev => ({ ...prev, itemsPerPage })
            )}
            disabled={paginationDisabled}
        >
            {buttonsInPaginationControls}
        </PaginationControls>
    </>);
}
